import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useCommandUpdateFirmware } from '../../../../../../../../app/ApiGen';
import { Dialog } from '../../../../../../../../components/Dialog';
import { errorSnackbar } from '../../../../../../../../components/ErrorSnackbar';
import { Form } from '../../../../../../../../components/Form';
import { Loading, type OnSubmit, Workspace } from '../../../../../../../../design-system';
import {
  parseOptionalInt,
  RETRIES_MAX,
  RETRIES_MIN,
  RETRY_INTERVAL_MAX_SECONDS,
  RETRY_INTERVAL_MIN_SECONDS,
  type UpdateFirmwareFields,
  validationSchema,
} from '../../../../../../../../utils/firmware';
import type { FormFields } from '../../../../../../../../utils/formUtils';
import { useChargePoint } from '../../../Provider';

interface UpdateFirmwareDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialValues: UpdateFirmwareFields = { firmwareUrl: '', retries: '', retryInterval: '' };

const fields: FormFields<UpdateFirmwareFields> = {
  firmwareUrl: { id: 'firmwareUrl', label: 'URL' },
  retries: {
    id: 'retries',
    label: `Retries (optional, ${RETRIES_MIN}–${RETRIES_MAX})`,
    inputProps: { type: 'number', inputProps: { min: RETRIES_MIN, max: RETRIES_MAX, step: 1 } },
  },
  retryInterval: {
    id: 'retryInterval',
    label: `Retry interval in seconds (optional, ${RETRY_INTERVAL_MIN_SECONDS}–${RETRY_INTERVAL_MAX_SECONDS})`,
    inputProps: {
      type: 'number',
      inputProps: { min: RETRY_INTERVAL_MIN_SECONDS, max: RETRY_INTERVAL_MAX_SECONDS, step: 1 },
    },
  },
};

export const UpdateFirmwareDialog: React.FC<UpdateFirmwareDialogProps> = ({ isOpen, onClose }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { chargePoint, refetch } = useChargePoint();

  const { error, mutate } = useCommandUpdateFirmware({ id: chargePoint?.id ?? '' });

  React.useEffect(() => {
    if (error) {
      errorSnackbar('Unable to update firmware', closeSnackbar, enqueueSnackbar, error);
    }
  }, [closeSnackbar, enqueueSnackbar, error]);

  const onSubmit = React.useCallback<OnSubmit<UpdateFirmwareFields>>(
    async ({ firmwareUrl, retries, retryInterval }, { setSubmitting }) => {
      try {
        await mutate({
          location: firmwareUrl,
          retries: parseOptionalInt(retries),
          retryInterval: parseOptionalInt(retryInterval),
        });
        enqueueSnackbar('Update firmware command sent');
        onClose();
        refetch();
      } finally {
        setSubmitting(false);
      }
    },
    [mutate, enqueueSnackbar, onClose, refetch],
  );

  if (!chargePoint) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Update firmware">
      <Form<UpdateFirmwareFields>
        fields={fields}
        initialValues={initialValues}
        validationSchema={validationSchema({ chargePointVendor: chargePoint.vendor })}
        onSubmit={onSubmit}
        labels={{ save: 'Send command' }}
        isEditing
      />
    </Dialog>
  );
};
